// Tiny synth: bell on rest finish, click on set log. Mute persists.

const STORAGE_KEY = 'mefit.sound.muted';

type Ctor = typeof AudioContext;
let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx && ctx.state !== 'closed') return ctx;
  const C: Ctor | undefined =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
  if (!C) return null;
  ctx = new C();
  return ctx;
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function toggleMuted(): boolean {
  const next = !isMuted();
  setMuted(next);
  return next;
}

// Resume audio context on first user gesture (iOS requirement).
function ensureRunning(c: AudioContext) {
  if (c.state === 'suspended') void c.resume();
}

function bellVoice(c: AudioContext, freq: number, when: number, dur: number, gain: number) {
  const partials = [
    { ratio: 1, gain: 1.0 },
    { ratio: 2, gain: 0.55 },
    { ratio: 3, gain: 0.32 },
    { ratio: 4.2, gain: 0.18 }, // inharmonic — gives the "bell" timbre
    { ratio: 5.4, gain: 0.08 },
  ];
  const out = c.createGain();
  out.gain.value = 0;
  out.gain.setValueAtTime(0.0001, when);
  out.gain.exponentialRampToValueAtTime(gain, when + 0.005);
  out.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  out.connect(c.destination);

  for (const p of partials) {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq * p.ratio;
    const partialGain = c.createGain();
    partialGain.gain.value = p.gain;
    osc.connect(partialGain).connect(out);
    osc.start(when);
    osc.stop(when + dur + 0.05);
  }
}

export function playBell() {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  ensureRunning(c);
  const now = c.currentTime;
  // Two-note "ding-dong": A5 then E5 a beat later.
  bellVoice(c, 880, now, 1.4, 0.16);
  bellVoice(c, 660, now + 0.18, 1.6, 0.12);
}

export function playClick() {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  ensureRunning(c);
  const now = c.currentTime;
  // Soft tick: short noise burst through a high-shelf filter.
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * 0.04), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = 3000;
  const gain = c.createGain();
  gain.gain.value = 0.07;
  src.connect(filter).connect(gain).connect(c.destination);
  src.start(now);
}
